<template>
  <div id="contenedor-contrato-estimaciones-catalogo-conceptos">
    <v-skeleton-loader
      v-if="$apollo.loading"
      type="table-thead, table-tbody">
    </v-skeleton-loader>
    <template
      v-for="(concepto) in getCatalogoConceptos">
      <template>
        <v-card
          :key="concepto.id"
          tile
          flat>
          <v-row
            :class="colorReglon(concepto.tipo)"
            class="caption">
            <v-col cols="1">
              <span
                :class="{'font-weight-bold': concepto.tipo !== 2}">
                <span
                  v-if="concepto.padre"
                  class="font-weight-bold">
                  {{ concepto.padre.clave }} -
                </span>
                {{ concepto.clave }}
              </span>
            </v-col>
            <v-col cols="6">
              <span
                :class="{'font-weight-bold': concepto.tipo !== 2}">
                {{ concepto.concepto }}
              </span>
            </v-col>
            <v-col v-if="concepto.tipo === 2" cols="3">
              <v-row no-gutters justify="center" align="center">
                <v-col cols="8">
                  <v-text-field
                    v-model.number="concepto.nuevaCantidad"
                    type="number"
                    v-if="concepto.tipo === 2"
                    outlined
                    dense
                    hide-details
                    v-on:keyup.enter="estimar(concepto)"
                  ></v-text-field>
                </v-col>
                <v-col cols="auto" class="ml-2">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        @click="estimar(concepto)"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        color="#1B1BAF">
                        <v-icon>fa-calculator</v-icon>
                      </v-btn>
                    </template>
                    <span>Estimar</span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <div class="ml-1">
                <div>
                  P. Unitario:
                  <span
                    class="font-weight-medium"
                  >{{ concepto.precioUnitario | currency }}</span>
                </div>
                <div>
                  U. Medida:
                  <span class="font-weight-medium">{{ concepto.unidad }}</span>
                </div>
                <div>
                  Cant. Cont:
                  <span class="font-weight-medium">{{ concepto.cantidad }}</span>
                </div>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="ml-1">
                <div>
                  Est. Ant.
                  <span
                    class="font-weight-medium"
                  >{{ concepto.estimadoAnterior }}</span>
                </div>
                <div>
                  Disponible
                  <span class="font-weight-medium">
                    <v-chip
                      small
                      :color="
                        concepto.cantidadDisponible >= 0
                        ? 'info' : 'warning'"
                      >{{ concepto.cantidadDisponible }}</v-chip>
                  </span>
                </div>
              </div>
            </v-col>
            <v-col
              v-if="concepto.tipo === 2"
              cols="2"
              class="text-right">
              <v-chip
                small
                :class="{'success': getConceptoEstimado(concepto.id).importe !== 0}">
                {{ getConceptoEstimado(concepto.id).importe | currency }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card>
      </template>
    </template>
    <v-row justify="center" no-gutters align="center" class="my-5">
      <v-col
        cols="12"
        v-if="catalogoDeConceptos.length > cantidadDeRegistrosPorPagina">
        <v-divider class="mb-3"></v-divider>
      </v-col>
      <v-btn
        v-if="catalogoDeConceptos.length > cantidadDeRegistrosPorPagina
          && (getCatalogoConceptos.length - (pagina * cantidadDeRegistrosPorPagina)) > 0"
        @click="pagina++"
        rounded
        depressed>
        Mostrar mas...
        {{ (catalogoDeConceptos.length - (pagina * cantidadDeRegistrosPorPagina)) }}
      </v-btn>
      <v-btn
        @click="cantidadDeRegistrosPorPagina = catalogoDeConceptos.length"
        class="ml-2"
        rounded
        depressed
        v-if="catalogoDeConceptos.length > cantidadDeRegistrosPorPagina">
        Mostrar todos
        <v-chip
          class="ml-1"
          small
          readonly>
          {{ catalogoDeConceptos.length }}
        </v-chip>
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import mixEstimaciones from '../mixEstimaciones';

export default {
  name: 'ContratoEstimacionesCatalogoConceptos',
  mixins: [
    mixEstimaciones,
  ],
  props: {
    filtros: {
      type: Object,
      required: true,
    },
    numeroEstimacion: {
      type: Number,
      required: true,
    },
    conceptosEstimadosEditar: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      pagina: 1,
      cantidadDeRegistrosPorPagina: 60,
      catalogoDeConceptos: [],
      conceptosEstimados: this.conceptosEstimadosEditar
        .map(({ idConcepto, cantidad, importe }) => ({
          idConcepto,
          cantidad,
          importe,
          estimado: true,
        })),
      conceptosAEliminar: [], // Array de IDs de conceptos a eliminar de BD
    };
  },
  methods: {
    colorReglon(tipo) {
      if (tipo === 0) return 'grey lighten-2';
      if (tipo === 1) return 'grey lighten-3';
      return '';
    },
    estimar(concepto) {
      // Validar que nuevaCantidad no sea undefined, null o NaN
      const cantidad = Number(concepto.nuevaCantidad);
      if (Number.isNaN(cantidad)) {
        console.warn('Cantidad inválida para estimar concepto:', concepto.nuevaCantidad);
        return;
      }

      // Regla de negocio: No se permiten cantidades de 0
      if (cantidad === 0) {
        const index = this.conceptosEstimados.findIndex(
          (conceptoEstimado) => conceptoEstimado.idConcepto === concepto.id,
        );

        if (index !== -1) {
          const conceptoExistente = this.conceptosEstimados[index];

          // Si el concepto ya estaba guardado en BD, agregarlo al array de eliminados
          if (conceptoExistente.estimado === true) {
            // Verificar que no esté ya en la lista de eliminados
            if (!this.conceptosAEliminar.includes(concepto.id)) {
              this.conceptosAEliminar.push(concepto.id);
            }
          }

          // Remover del array de conceptos estimados
          this.conceptosEstimados.splice(index, 1);

          // Emitir ambos arrays al componente padre
          this.$emit('conceptosEstimados', this.conceptosEstimados);
          this.$emit('conceptosAEliminar', this.conceptosAEliminar);
        }

        console.warn('No se permite estimar con cantidad 0');
        return;
      }

      // Si la cantidad no es 0, remover del array de eliminados si estaba ahí
      const indexEliminar = this.conceptosAEliminar.indexOf(concepto.id);
      if (indexEliminar !== -1) {
        this.conceptosAEliminar.splice(indexEliminar, 1);
        this.$emit('conceptosAEliminar', this.conceptosAEliminar);
      }

      // Determinar si el concepto ya existe en BD
      // Consultar en los datos originales de la BD (conceptosEstimadosEditar)
      const existeEnBD = this.conceptosEstimadosEditar.some(
        ({ idConcepto }) => idConcepto === concepto.id,
      );

      // Formato de concepto
      const nuevoConceptoFormato = {
        idConcepto: concepto.id,
        cantidad,
        importe: this.$obtenerImporteDosDecimales(cantidad * concepto.precioUnitario),
        estimado: existeEnBD, // true = UPDATE (existe en BD), false = INSERT (nuevo)
      };

      // Checar si ya fue estimado un concepto
      const index = this.conceptosEstimados.findIndex(
        (conceptoEstimado) => conceptoEstimado.idConcepto === concepto.id,
      );

      // Checa si el concepto fue estimado
      if (index === -1) {
        // Si no fue estimado lo agrega
        this.conceptosEstimados.push(nuevoConceptoFormato);
      } else {
        // Si ya fue estimado lo actualiza
        this.conceptosEstimados.splice(index, 1, nuevoConceptoFormato);
      }

      // Enviar los conceptos estimados
      this.$emit('conceptosEstimados', this.conceptosEstimados);
    },
    getConceptoEstimado(id) {
      return this.conceptosEstimados.find(({ idConcepto }) => idConcepto === id) || {
        importe: 0,
      };
    },
  },
  computed: {
    catalogoDeConceptosFormateado() {
      if (this.catalogoDeConceptos.length <= 0) return [];

      return this.catalogoDeConceptos
        .map((concepto) => {
          const conceptoEstimado = this.conceptosEstimadosEditar
            .find(({ idConcepto }) => idConcepto === concepto.id);

          if (!conceptoEstimado) {
            const estimadoAnterior = this.$numberRoundDigits(concepto.estimado
              .reduce((anterior, estimacion) => anterior + estimacion.cantidad, 0), 5);
            const cantidadDisponible = this
              .$numberRoundDigits((concepto.cantidad - estimadoAnterior), 5);

            return {
              ...concepto,
              nuevaCantidad: 0,
              estimadoAnterior,
              cantidadDisponible,
              estimado: false,
            };
          }

          const conceptos = conceptoEstimado.concepto.estimado
            .filter(({ estimacion }) => estimacion.numero < this.numeroEstimacion);

          const estimadoAnterior = this.$numberRoundDigits(conceptos
            .reduce((anterior, estimacion) => anterior + estimacion.cantidad, 0), 5);
          const cantidadDisponible = this
            .$numberRoundDigits((concepto.cantidad - estimadoAnterior), 5);

          return {
            ...concepto,
            nuevaCantidad: conceptoEstimado?.cantidad || 0,
            estimadoAnterior,
            cantidadDisponible,
            estimado: true,
          };
        })
        .sort((a, b) => a.orden - b.orden);
    },
    getCatalogoConceptos() {
      if (!this.catalogoDeConceptos) return [];

      // Bucar concepto
      const catalogo = this.catalogoDeConceptosFormateado
        .filter((concepto) => {
          if (!this.filtros.buscarConcepto) return true;
          if (concepto.tipo !== 2) return true;
          const expt = new RegExp(this.filtros.buscarConcepto, 'gi');
          const clave = this.$normalizacionTexto(concepto.clave);
          const conceptoLimpio = this.$normalizacionTexto(concepto.concepto);

          return expt.test(concepto.clave)
          || expt.test(concepto.concepto)
          || expt.test(clave)
          || expt.test(conceptoLimpio);
        });

      return this.mixMostrarMas(catalogo, this.pagina, this.cantidadDeRegistrosPorPagina);
    },
  },
};
</script>

<style scoped>
</style>
